import ExcelJS from "exceljs";
import FileSaver from "file-saver";
import moment from "moment";
import {
  getDetalladoTableFields,
  getResumenTableFields,
} from "./liquidacion-reportes.constants";

const EXCEL_MIME =
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const COLORS = {
  headerBg: "FF000000",
  headerText: "FFFFFFFF",
  penalidadHeaderBg: "FFDC3545",
  border: "FFDCDCDC",
  stripe: "FFFAFAFA",
  title: "FFFF7A00",
};

const RESUMEN_MONEY_KEYS = new Set([
  "costoSinIgv",
  "costoConIgv",
  "penalidad",
  "totalPorFactSinIgv",
  "igv",
  "totalPorFactConIgv",
]);

const DETALLADO_MONEY_KEYS = new Set([
  "costoUnitarioSinIgv",
  "costoUnitarioConIgv",
  "costoTotalSinIgv",
  "costoTotalConIgv",
  "penalidad",
  "costoFinalSinIgv",
  "costoFinalConIgv",
]);

function cellBorder() {
  return {
    top: { style: "thin", color: { argb: COLORS.border } },
    left: { style: "thin", color: { argb: COLORS.border } },
    bottom: { style: "thin", color: { argb: COLORS.border } },
    right: { style: "thin", color: { argb: COLORS.border } },
  };
}

function getCellAlignment(field) {
  if (field.tdClass?.includes("text-right")) {
    return { horizontal: "right", vertical: "middle" };
  }

  if (field.tdClass?.includes("text-left")) {
    return { horizontal: "left", vertical: "middle" };
  }

  return { horizontal: "center", vertical: "middle" };
}

function getDisplayLength(value) {
  if (value === null || value === undefined) return 0;
  return String(value).length;
}

function autoFitColumns(sheet, fields, items) {
  sheet.columns.forEach((column, index) => {
    const field = fields[index];
    let maxLength = field.label.length;

    items.forEach((item) => {
      maxLength = Math.max(maxLength, getDisplayLength(item[field.key]));
    });

    column.width = Math.min(Math.max(maxLength + 3, 12), 45);
  });
}

async function buildStyledWorkbook({
  sheetName,
  title,
  fields,
  items,
  moneyKeys,
  penaltyKey = "penalidad",
}) {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Promart Servicios";
  workbook.created = new Date();

  const sheet = workbook.addWorksheet(sheetName, {
    views: [{ state: "frozen", ySplit: 2 }],
  });

  sheet.mergeCells(1, 1, 1, fields.length);
  const titleCell = sheet.getCell(1, 1);
  titleCell.value = title;
  titleCell.font = { bold: true, size: 14, color: { argb: COLORS.title } };
  titleCell.alignment = { horizontal: "left", vertical: "middle" };
  sheet.getRow(1).height = 26;

  const headerRow = sheet.getRow(2);
  fields.forEach((field, columnIndex) => {
    const cell = headerRow.getCell(columnIndex + 1);
    const isPenalidad = field.key === penaltyKey;

    cell.value = field.label;
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: {
        argb: isPenalidad ? COLORS.penalidadHeaderBg : COLORS.headerBg,
      },
    };
    cell.font = { bold: true, color: { argb: COLORS.headerText }, size: 11 };
    cell.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };
    cell.border = cellBorder();
  });
  headerRow.height = 30;

  items.forEach((item, rowIndex) => {
    const row = sheet.getRow(rowIndex + 3);
    const isStripe = rowIndex % 2 === 0;

    fields.forEach((field, columnIndex) => {
      const cell = row.getCell(columnIndex + 1);
      const rawValue = item[field.key];
      const isMoney = moneyKeys.has(field.key);

      if (isMoney) {
        cell.value =
          rawValue === null || rawValue === undefined ? null : Number(rawValue);
        cell.numFmt = "#,##0.00";
        cell.alignment = { horizontal: "right", vertical: "middle" };
      } else if (field.key === "archivoPdf" || field.key === "urlPdf") {
        const link = item.urlPdf || item.archivoPdf;
        const hasValidLink =
          link &&
          typeof link === "string" &&
          link.trim() !== "" &&
          link.trim() !== "-" &&
          link.toLowerCase() !== "null" &&
          link.toLowerCase() !== "undefined";

        if (hasValidLink) {
          cell.value = { text: "Ver PDF", hyperlink: link.trim() };
          cell.font = { color: { argb: "FF0000FF" }, underline: true };
        } else {
          cell.value = "-";
        }
        cell.alignment = { horizontal: "center", vertical: "middle" };
      } else {
        cell.value = rawValue ?? "";
        cell.alignment = getCellAlignment(field);
      }

      cell.border = cellBorder();

      if (isStripe) {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: COLORS.stripe },
        };
      }
    });
  });

  autoFitColumns(sheet, fields, items);

  return workbook;
}

async function downloadWorkbook(workbook, fileName) {
  const buffer = await workbook.xlsx.writeBuffer();

  FileSaver.saveAs(new Blob([buffer], { type: EXCEL_MIME }), fileName);
}

export function buildResumenExportFileName() {
  return `ReporteLiquidacionResumen_${moment().format("YYYYMMDD_HHmmss")}.xlsx`;
}

export function buildDetalladoExportFileName() {
  return `ReporteLiquidacionDetallado_${moment().format("YYYYMMDD_HHmmss")}.xlsx`;
}

export async function saveResumenExportBlob(blob) {
  FileSaver.saveAs(blob, buildResumenExportFileName());
}

export async function downloadResumenExcel(items = []) {
  const fields = getResumenTableFields();
  const workbook = await buildStyledWorkbook({
    sheetName: "Resumen",
    title: `Reporte Liquidación Resumen — ${moment().format("DD/MM/YYYY HH:mm")}`,
    fields,
    items,
    moneyKeys: RESUMEN_MONEY_KEYS,
  });

  await downloadWorkbook(workbook, buildResumenExportFileName());
}

export async function saveDetalladoExportBlob(blob) {
  FileSaver.saveAs(blob, buildDetalladoExportFileName());
}

export async function downloadDetalladoExcel(items = []) {
  const fields = getDetalladoTableFields();
  const workbook = await buildStyledWorkbook({
    sheetName: "Detallado",
    title: `Reporte Liquidación Detallado — ${moment().format("DD/MM/YYYY HH:mm")}`,
    fields,
    items,
    moneyKeys: DETALLADO_MONEY_KEYS,
  });

  await downloadWorkbook(workbook, buildDetalladoExportFileName());
}
